// context/UserContext.tsx
import { EntitiesGetMeResponse } from 'api/api';
import { useApiV1UserMeList } from 'api/api.gen';
import { GetMeResponse } from 'api/axios-client';
import { useMeGETQuery } from 'api/axios-client/Query';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

interface UserContextType {
    user: GetMeResponse | null;
    isLoading: boolean;
    isFetching: boolean;
    setUser: (user: GetMeResponse | null) => void;
    refetch: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [user, setUser] = useState<GetMeResponse | null>(null);

    const { data, isLoading, isFetched, isFetching, refetch } = useMeGETQuery(
        {}
    );

    // Update user state when data is fetched
    useEffect(() => {
        if (data && !isLoading && isFetched) {
            setUser(data);
        }
    }, [data, isLoading, isFetched]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            refetch,
            user,
            isLoading,
            isFetching,
            setUser,
        }),
        [refetch, user, isLoading]
    );

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserProvider');
    }
    return context;
};
